import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useModal } from '@/hooks/use-modal';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { getBrowserTimezone } from '@/utils/date-formatters/date-formatters';
import { logger } from '@/utils/logger/logger';

import { computeElapsedSeconds } from '../../helpers/compute-elapsed.helper';
import { formatTime } from '../../helpers/format-time-track.helper';
import { useStartTimer } from '../../hooks/use-start-timer';
import { useCreateWorklog } from '../../hooks/use-time-track-mutations';
import { useTimerControls } from '../../hooks/use-timer-controls';
import { useTimeTrackingStore } from '../../store/time-tracking.store';
import { CreateWorklogRequest } from '../../types/dto/worklogs-summary.dto';
import { CascadeSize, TimeTrackerView } from '../../types/time-tracking';
import { TimeEntryModal } from '../modals/time-entry-modal';
import { CascadeView } from './cascade-view';
import { CompactView } from './compact-view';
import { FlatView } from './flat-view';

const TAG = 'TimeTracker:';

const toProjectIdOrNull = (item: DropdownSearchItem | null): number | null => {
  if (!item) return null;
  const numericId = Number(item.id);
  if (Number.isNaN(numericId)) {
    logger.error(TAG, 'Project id is not numeric', { id: item.id });
    return null;
  }
  return numericId;
};

interface TimeTrackerProps {
  view: TimeTrackerView;
  className?: string;
  /** Controls sizing tier for the cascade view */
  cascadeSize?: CascadeSize;
}

export function TimeTracker({ view, className, cascadeSize = 'full' }: TimeTrackerProps) {
  const { t } = useTranslation(['authenticated.timeTracking', 'common']);
  const { displayToastErrorFromApiWithFallback } = useApiError();
  const [displayTime, setDisplayTime] = useState('00:00:00');
  const [isTimerSetupMode, setIsTimerSetupMode] = useState(false);
  const preselectedProject = useTimeTrackingStore(state => state.preselectedProject);
  const preselectedDescription = useTimeTrackingStore(state => state.preselectedDescription);
  const setPreselectedProject = useTimeTrackingStore(state => state.setPreselectedProject);
  const setPreselectedDescription = useTimeTrackingStore(state => state.setPreselectedDescription);
  const setActiveTimerProject = useTimeTrackingStore(state => state.setActiveTimerProject);
  const clearPreselectedTimerState = useTimeTrackingStore(
    state => state.clearPreselectedTimerState
  );

  const {
    open: isCancelTrackingModalOpen,
    onOpen: openCancelTrackingModal,
    onClose: closeCancelTrackingModal,
  } = useModal();

  const {
    activeTimer,
    activeTimerFetchedAt,
    fetchedTimerProject,
    isPaused,
    isLimitReached,
    isStopModalOpen,
    openStopModal,
    closeStopModal,
    timeTrack,
    isDiscardModalOpen: _isDiscardModalOpen,
    openDiscardModal: _openDiscardModal,
    closeDiscardModal: _closeDiscardModal,
    handleTogglePause,
    handleStopSubmit,
    handleSaveEntry: handleSaveTimerEntry,
    handleDiscard: handleDiscardTimer,
    isTogglePausePending,
    isStopPending,
    isDiscardPending,
    isAnyActionPending,
  } = useTimerControls();

  const { mutateAsync: startTimerMutation, isPending: isStartPending } = useStartTimer();
  const { mutateAsync: createWorklogMutation } = useCreateWorklog();

  const isActive = !!activeTimer;
  const displayedProject = isActive ? (fetchedTimerProject ?? null) : preselectedProject;

  // Live ticker — only runs when timer is active and ticking forward.
  useEffect(() => {
    if (!activeTimer || activeTimerFetchedAt === undefined) {
      setDisplayTime('00:00:00');
      return;
    }

    const update = () =>
      setDisplayTime(formatTime(computeElapsedSeconds(activeTimer, activeTimerFetchedAt)));
    update();

    if (activeTimer.isPaused || activeTimer.isLimitReached) {
      return;
    }

    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [activeTimer, activeTimerFetchedAt]);

  const handleOpenAddEntry = () => {
    setIsTimerSetupMode(false);
    openStopModal();
  };

  const handleOpenProjectSelector = () => {
    setIsTimerSetupMode(!isActive);
    openStopModal();
  };

  const handleToggle = async () => {
    if (isAnyActionPending || isStartPending) return;
    try {
      if (!isActive) {
        await startTimerMutation({
          projectId: toProjectIdOrNull(preselectedProject),
          description: preselectedDescription || null,
          timezone: getBrowserTimezone(),
        });
        // Remember the project for the running timer so the widget can render it
        // without re-fetching full project details (which 403s for worklog-only users).
        setActiveTimerProject(preselectedProject);
        clearPreselectedTimerState();
        return;
      }
      setIsTimerSetupMode(false);
      openStopModal();
    } catch (error) {
      logger.error(TAG, 'Failed to toggle timer', error);
      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.timeTracking:api.error.activeTimerStartFailed'
      );
    }
  };

  const handleStopWithClear = async (values: CreateWorklogRequest) => {
    // handleStopSubmit clears the persisted timer project on success.
    await handleStopSubmit(values);
    clearPreselectedTimerState();
  };

  const handleAddEntry = async (values: CreateWorklogRequest) => {
    await createWorklogMutation(values);
    closeStopModal();
  };

  const handleSaveTimeEntry = async (
    values: CreateWorklogRequest,
    project: DropdownSearchItem | null
  ) => {
    if (isActive) {
      // handleSaveTimerEntry keeps the persisted timer project in sync.
      await handleSaveTimerEntry(values, project);
    } else {
      setPreselectedProject(project);
      setPreselectedDescription(values.description ?? '');
      closeStopModal();
    }
  };

  const handleCancelTracking = async () => {
    // handleDiscardTimer clears the persisted timer project on success.
    await handleDiscardTimer();
    clearPreselectedTimerState();
    closeCancelTrackingModal();
  };

  const renderView = () => {
    const props = {
      displayTime,
      className,
      selectedProject: displayedProject,
      isActive,
      isPaused,
      isLimitReached,
      openCreateModal: handleOpenAddEntry,
      openProjectSelector: handleOpenProjectSelector,
      toggle: handleToggle,
      togglePause: handleTogglePause,
      reset: openCancelTrackingModal,
      isStartPending,
      isTogglePausePending,
      isStopPending,
      isDiscardPending,
      isAnyActionPending: isAnyActionPending || isStartPending,
    };
    switch (view) {
      case TimeTrackerView.Flat:
        // view in 1 row for header
        return <FlatView {...props} />;
      case TimeTrackerView.Compact:
        // sidebar compact view
        return <CompactView {...props} />;
      case TimeTrackerView.Cascade:
        // view in 2 columns for sidebar
        return <CascadeView {...props} size={cascadeSize} />;
    }
  };

  return (
    <>
      {renderView()}
      {isStopModalOpen && (
        <TimeEntryModal
          open={isStopModalOpen}
          onClose={closeStopModal}
          onSubmit={isActive ? handleStopWithClear : handleAddEntry}
          timeTrack={timeTrack}
          defaultProject={displayedProject}
          defaultDescription={isActive ? undefined : preselectedDescription}
          isRunning={isActive}
          isTimerSetup={isTimerSetupMode}
          onSave={handleSaveTimeEntry}
          activeTimer={activeTimer}
          activeTimerFetchedAt={activeTimerFetchedAt}
        />
      )}
      <ConfirmationModal
        open={isCancelTrackingModalOpen}
        onClose={closeCancelTrackingModal}
        title={t('tracker.cancelTracking.title')}
        message={t('tracker.cancelTracking.message')}
        confirmText={t('tracker.cancelTracking.confirm')}
        onConfirm={handleCancelTracking}
      />
    </>
  );
}
